import React from 'react';
import {
    Container,
    Box,
    Typography,
    Button,
    Paper,
    IconButton,
    useTheme,
    useMediaQuery,
    Link
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';
import SiteHeader from '../../components/SiteHeader';
import AppFooter from '../../components/AppFooter';

const LIGHT_PURPLE = '#A76FFF';

const WebPrivacyPolicyScreen: React.FC = () => {
    const navigate = useNavigate();

    return (
        <>
            <SEOHead
                title="Privacy Policy - Pop The Balloon Dating App"
                description="Read Pop The Balloon's privacy policy to understand how we protect your personal information and data in our dating app."
                canonical="https://www.poptheballoonapp.com/privacy-policy"
                ogTitle="Privacy Policy - Pop The Balloon"
                ogDescription="Learn how Pop The Balloon protects your privacy and personal data while using our dating app."
                ogUrl="https://www.poptheballoonapp.com/privacy-policy"
            />
            <SiteHeader />
            <Container maxWidth="md" sx={{ py: 4 }}>
            <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <IconButton onClick={() => navigate(-1)} size="large" sx={{ mr: 1 }}>
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
                        Privacy Policy
                    </Typography>
                </Box>

                <Typography variant="body2" color="textSecondary" sx={{ mb: 4, fontStyle: 'italic' }}>
                    Last Updated: October 1, 2026
                </Typography>

                <Box sx={{ mb: 4 }}>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        Pop The Balloon ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our dating application and related services (the "Service").
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        Please read this Privacy Policy carefully. By using the Service, you agree to the collection and use of information in accordance with this policy.
                    </Typography>

                    <Typography variant="h6" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
                        1. Information We Collect
                    </Typography>
                    
                    <Typography variant="subtitle1" sx={{ mb: 1, mt: 2, fontWeight: 500 }}>
                        1.1 Information You Provide
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                        Account Information:
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Name, email address, phone number
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Date of birth, gender, sexual orientation
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Profile photos and bio
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Location (city/region)
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        • Preferences for matches
                    </Typography>

                    <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                        Subscription and Payment Information:
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Subscription tier (Free, Golden, Platinum)
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Subscription status and billing period
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Payment method details (processed securely by Stripe)
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Purchase history for subscriptions and in-app purchases
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Billing address and country
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        • Transaction IDs and payment status
                    </Typography>

                    <Typography variant="subtitle1" sx={{ mb: 1, mt: 2, fontWeight: 500 }}>
                        1.1a Photos and Face Data
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Two features in Pop The Balloon involve photographs that contain your face. We do not
                        create, store, or use a faceprint, facial template, facial geometry, or any other
                        biometric identifier, and we never use facial recognition to identify you or to match
                        you against any database.
                    </Typography>

                    <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                        Profile photo check:
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        When you add a profile photo, the image is sent to Amazon Rekognition, operated by
                        Amazon Web Services, for a single check: whether the photo contains a human face. The
                        only results we receive and use are whether a face was found and how many. No facial
                        measurements or other face details are requested, received, or stored. This check keeps
                        profiles genuine by preventing photos with no person in them. Your profile photo itself
                        is then stored so it can be shown on your profile, as you would expect.
                    </Typography>

                    <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                        Optional photo verification:
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        If you choose to verify your profile, you take a selfie holding a randomly assigned pose,
                        such as a peace sign or a wave. A trained human reviewer compares it with your profile
                        photos by eye to confirm you are the same person. No automated facial recognition or face
                        matching is performed at any point. We also store a cryptographic hash of the image, which
                        is a one-way fingerprint of the file and not of your face, so the same photo cannot be
                        reused to verify a different account. Verification is entirely optional and you can use
                        the app without it.
                    </Typography>

                    <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                        Optional intro videos:
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        If you record an intro video, it is screened automatically for prohibited content such as
                        nudity or violence. For the duration of that scan the clip is held in a private Amazon Web
                        Services storage location and is deleted from it as soon as the scan completes. The scan
                        looks for prohibited content only and does not identify people.
                    </Typography>

                    <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                        Who processes these images:
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Amazon Web Services performs the face check and the video content scan. Supabase provides
                        the storage where your photos and videos are held. We have applied an Amazon Web Services
                        AI services opt-out policy to our account, which contractually prevents Amazon from
                        retaining or using any content we send it to improve its services. We never sell, rent, or
                        share your photos with advertisers, data brokers, or any other third party, and we never
                        use them for advertising, profiling, or training machine learning models.
                    </Typography>

                    <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                        How long we keep them:
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • The image sent for the profile photo face check is not retained by us or by Amazon after
                        the check. It is processed in the request and discarded.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Profile photos are kept until you delete them or delete your account.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Verification selfies are kept while the request is reviewed. Thirty days after review we
                        remove the reference to the image, and the image itself is deleted when you delete your
                        account.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        • Deleting your account removes every image you have uploaded, including profile photos,
                        verification selfies, and intro videos. You can delete your account at any time from
                        Settings, then Account, then Delete Account. See section 6.3.
                    </Typography>

                    <Typography variant="subtitle1" sx={{ mb: 1, mt: 2, fontWeight: 500 }}>
                        1.2 Information Collected Automatically
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                        Usage Information:
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Features you use and how you use them
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Daily picks viewed and actions taken
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Time spent on the Service
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Subscription feature usage (rewinds, boosts, etc.)
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        • In-app purchase activity
                    </Typography>

                    <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                        Device Information:
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Device type, operating system, and version
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Unique device identifiers
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • IP address and approximate location
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Browser type and version (when you visit our website)
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        • Mobile network information
                    </Typography>

                    <Typography variant="h6" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
                        2. How We Use Your Information
                    </Typography>
                    
                    <Typography variant="subtitle1" sx={{ mb: 1, mt: 2, fontWeight: 500 }}>
                        2.1 To Provide the Service
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Create and manage your account
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Generate daily picks based on your preferences
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Facilitate messaging between matches
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Process your keep/pop decisions
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        • Enable rewind functionality and profile boosts
                    </Typography>

                    <Typography variant="subtitle1" sx={{ mb: 1, mt: 2, fontWeight: 500 }}>
                        2.2 To Process Subscriptions and Payments
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Process subscription purchases and renewals
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Manage free trials
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Apply subscription discounts to purchases
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Allocate monthly Golden Balloons and boosts to subscribers
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Handle subscription upgrades, downgrades, and cancellations
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Process refund requests
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Detect and prevent payment fraud
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        • Maintain payment records as required by law
                    </Typography>

                    <Typography variant="subtitle1" sx={{ mb: 1, mt: 2, fontWeight: 500 }}>
                        2.3 To Communicate with You
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Send subscription confirmation emails
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Send trial reminder notifications
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Send payment receipts and billing notifications
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Send monthly allocation notifications
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Notify you of subscription status changes
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        • Respond to your support requests
                    </Typography>

                    <Typography variant="h6" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
                        3. Subscription Data Handling
                    </Typography>
                    
                    <Typography variant="subtitle1" sx={{ mb: 1, mt: 2, fontWeight: 500 }}>
                        3.1 Data We Store
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                        Subscription Records:
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Current subscription tier and status
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Subscription start and end dates
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Billing period (monthly/annual)
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Trial usage history
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        • Subscription change history
                    </Typography>

                    <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                        Payment Records:
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Stripe customer ID and subscription ID
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Payment method type (e.g., "Visa ending in 1234")
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Transaction history and amounts
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        • Invoice and refund records
                    </Typography>

                    <Typography variant="subtitle1" sx={{ mb: 1, mt: 2, fontWeight: 500 }}>
                        3.2 Data We Do NOT Store
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Full credit card numbers
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • CVV/CVC codes
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        • Complete payment card data (handled by Stripe)
                    </Typography>

                    <Typography variant="subtitle1" sx={{ mb: 1, mt: 2, fontWeight: 500 }}>
                        3.3 Data Retention for Subscriptions
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • <strong>Active Subscriptions</strong>: Data retained while subscription is active
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • <strong>Cancelled Subscriptions</strong>: Subscription history retained for 3 years
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • <strong>Payment Records</strong>: Retained for 7 years as required by law
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        • <strong>Analytics Data</strong>: Anonymized and retained indefinitely
                    </Typography>

                    <Typography variant="h6" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
                        4. How We Share Your Information
                    </Typography>
                    
                    <Typography variant="subtitle1" sx={{ mb: 1, mt: 2, fontWeight: 500 }}>
                        4.1 Service Providers
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        We share information with third-party service providers who help us operate the Service:
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • <strong>Stripe</strong> (Payment Processing): Payment information for subscription and purchase processing
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • <strong>Supabase</strong> (Database and Authentication): Account, profile, and subscription data
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • <strong>Resend</strong> (Email Service): Email address for transactional emails
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • <strong>Anthropic</strong> (AI Conversation Insights): When you request an AI Insight, the relevant conversation messages and basic profile details of you and your match are sent to generate it. They are not used to train AI models
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • <strong>RevenueCat</strong> (Subscriptions and Purchases): Purchase and subscription status, linked to your account ID
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • <strong>Google</strong> (Firebase Notifications and Analytics, Google Play Billing): Device notification token, app usage events and purchase records
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • <strong>Amazon Web Services</strong> (Photo and Video Safety Checks): Profile photos and videos, as described in section 1
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        • <strong>Analytics Providers</strong>: Anonymized usage and subscription metrics
                    </Typography>

                    <Typography variant="subtitle1" sx={{ mb: 1, mt: 2, fontWeight: 500 }}>
                        4.2 Advertising and Measurement Partners
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        We work with advertising partners to understand which of our ads bring people to Pop The Balloon and to show our ads to people who are likely to be interested.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • <strong>Meta Platforms</strong> (Facebook and Instagram), Android app only: When you install and open the app, create an account, or make a purchase through Google Play, the app tells Meta that this happened. It shares app install, app open and account sign-up events; Google Play purchase events (what was bought and the price); your device's advertising ID and basic device information (such as model, operating system and app version); and a pseudonymous identifier derived from your account, not your name, email address or account ID. Meta uses this information to measure our ads and under its own{' '}
                        <Link href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        • <strong>Google AdMob</strong>: If you choose to watch an optional rewarded ad (for example, for an extra Daily Pick), Google may use your device's advertising ID and device information to choose and measure the ad, under{' '}
                        <Link href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</Link>.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        <strong>What we never share with advertising partners:</strong> your messages, photos, profile details, precise location, email address or phone number.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        <strong>Your choices:</strong>
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • <strong>United Kingdom:</strong> We ask for your permission when you first open the app. Ad measurement with Meta only starts if you choose <strong>Allow</strong>.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • <strong>Other countries:</strong> Ad measurement with Meta is on by default. We rely on our legitimate interest in understanding how our advertising performs, and you can switch it off at any time.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • You can change your choice in <strong>Settings → Account → Your Data → Ad measurement</strong>. Turning it off stops the app sharing events with Meta from then on.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        • You can also reset or delete your advertising ID in your Android device settings (<strong>Settings → Privacy → Ads</strong>).
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        We will update this policy before we start working with any new advertising partner.
                    </Typography>

                    <Typography variant="subtitle1" sx={{ mb: 1, mt: 2, fontWeight: 500 }}>
                        4.3 Legal Requirements
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        We may disclose your information if required by law or in response to:
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Court orders or subpoenas
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Legal processes
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Government requests
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        • Protection of our rights or safety of users
                    </Typography>

                    <Typography variant="h6" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
                        5. GDPR Compliance (UK and EU Users)
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        If you are located in the European Economic Area (EEA), you have additional rights under the General Data Protection Regulation (GDPR).
                    </Typography>
                    
                    <Typography variant="subtitle1" sx={{ mb: 1, mt: 2, fontWeight: 500 }}>
                        5.1 Legal Basis for Processing
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        We process your data based on:
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • <strong>Contract</strong>: To provide the Service and process subscriptions
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • <strong>Legitimate Interest</strong>: To improve the Service and prevent fraud
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • <strong>Consent</strong>: For marketing communications and optional features
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        • <strong>Legal Obligation</strong>: To comply with laws and regulations
                    </Typography>

                    <Typography variant="subtitle1" sx={{ mb: 1, mt: 2, fontWeight: 500 }}>
                        5.2 Your GDPR Rights
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        You have the right to:
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • <strong>Access</strong>: Request a copy of your personal data
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • <strong>Rectification</strong>: Correct inaccurate data
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • <strong>Erasure</strong>: Request deletion of your data ("right to be forgotten")
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • <strong>Restriction</strong>: Limit how we use your data
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • <strong>Portability</strong>: Receive your data in a portable format
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • <strong>Object</strong>: Object to processing based on legitimate interest
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        • <strong>Withdraw Consent</strong>: Withdraw consent at any time
                    </Typography>

                    <Typography variant="h6" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
                        6. Your Privacy Rights
                    </Typography>
                    
                    <Typography variant="subtitle1" sx={{ mb: 1, mt: 2, fontWeight: 500 }}>
                        6.1 Access and Correction
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        You can access and update your information through account settings in the app or by contacting our support team.
                    </Typography>

                    <Typography variant="subtitle1" sx={{ mb: 1, mt: 2, fontWeight: 500 }}>
                        6.2 Data Export
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        You can request a copy of your data, including:
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Profile information
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Message history
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Subscription history
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Payment records (excluding full card numbers)
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        • Usage analytics
                    </Typography>

                    <Typography variant="subtitle1" sx={{ mb: 1, mt: 2, fontWeight: 500 }}>
                        6.3 Data Deletion Requests
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        You have the right to request deletion of your personal data at any time. There are two ways to do this:
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                        Option 1 — Delete via the app:
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        1. Open Pop The Balloon and go to <strong>Settings</strong>
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        2. Tap <strong>Account</strong>
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        3. Tap <strong>Delete Account</strong> and confirm
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Your account and all associated personal data will be permanently deleted within 30 days.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                        Option 2 — Submit a deletion request by email:
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        Email us at <strong>support@poptheballoonapp.com</strong> with the subject line <strong>"Data Deletion Request"</strong> and include the email address associated with your account. We will process your request within 30 days and confirm once complete.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Upon deletion:
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Your profile, photos, messages, and personal data will be permanently deleted within 30 days
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Active subscriptions will be cancelled (no refund for the current billing period)
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Payment records will be retained for 7 years as required by law
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        • Anonymized analytics data may be retained indefinitely
                    </Typography>

                    <Typography variant="subtitle1" sx={{ mb: 1, mt: 2, fontWeight: 500 }}>
                        6.4 Legacy Facebook Login Data Deletion
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        Pop The Balloon no longer offers Facebook Login. If you previously signed in using Facebook, you can request deletion of the data we received from Facebook by either method described in section 6.3 above.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        The data received from legacy Facebook Login was limited to:
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Your name (from your public profile)
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Your email address
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        We do not store any other Facebook data. Submitting a deletion request removes all of this data from our systems within 30 days. You can also disconnect Pop The Balloon from your Facebook account at any time via <strong>Facebook → Settings → Apps and Websites</strong>.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        For questions about Facebook data deletion, email us at <strong>support@poptheballoonapp.com</strong>.
                    </Typography>

                    <Typography variant="h6" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
                        7. Data Security
                    </Typography>
                    
                    <Typography variant="subtitle1" sx={{ mb: 1, mt: 2, fontWeight: 500 }}>
                        7.1 Security Measures
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        We implement industry-standard security measures:
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Encryption in transit (TLS/SSL)
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Encryption at rest for sensitive data
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Secure authentication (OAuth 2.0)
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Regular security audits
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Access controls and monitoring
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        • PCI DSS compliance for payment processing
                    </Typography>

                    <Typography variant="subtitle1" sx={{ mb: 1, mt: 2, fontWeight: 500 }}>
                        7.2 Payment Security
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • All payment processing is handled by Stripe, a PCI DSS Level 1 certified provider
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • We never store full credit card numbers
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Payment data is encrypted and tokenized
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        • We use Stripe's secure checkout for all transactions
                    </Typography>

                    <Typography variant="subtitle1" sx={{ mb: 1, mt: 2, fontWeight: 500 }}>
                        7.3 Data Breach Notification
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        In the event of a data breach affecting your personal information:
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • We will notify you within 72 hours of discovery
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • We will notify relevant authorities as required by law
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        • We will provide information about the breach and steps to protect yourself
                    </Typography>

                    <Typography variant="h6" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
                        8. Children's Privacy
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        The Service is not intended for users under 18 years of age. We do not knowingly collect information from children. If we discover we have collected information from a child, we will delete it immediately.
                    </Typography>

                    <Typography variant="h6" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
                        9. Data Retention
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        We retain different types of data for different periods:
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • <strong>Account Information</strong>: Until account deletion + 30 days
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • <strong>Subscription Records</strong>: 3 years after cancellation
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • <strong>Payment Records</strong>: 7 years (legal requirement)
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • <strong>Usage Analytics</strong>: Indefinitely (anonymized)
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        • <strong>Messages</strong>: Until account deletion + 30 days
                    </Typography>

                    <Typography variant="h6" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
                        10. Changes to This Privacy Policy
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        We may update this Privacy Policy from time to time. We will notify you of material changes by:
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Posting the updated policy with a new "Last Updated" date
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • Sending an email notification
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        • Displaying a notice in the app
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        Your continued use of the Service after changes constitutes acceptance of the updated Privacy Policy.
                    </Typography>

                    <Typography variant="h6" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
                        11. Contact Us
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        For questions about this Privacy Policy, data deletion requests, or any privacy concerns, please contact us:
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • <strong>Email:</strong> support@poptheballoonapp.com
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        • <strong>Subject line for data deletion:</strong> "Data Deletion Request"
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        We aim to respond to all privacy requests within 30 days.
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={() => navigate(-1)}
                        sx={{
                            bgcolor: LIGHT_PURPLE,
                            borderRadius: 5,
                            px: 4,
                            py: 1.5,
                            fontWeight: 600,
                            '&:hover': {
                                bgcolor: '#8A5CF5',
                            },
                        }}
                    >
                        I Understand
                    </Button>
                </Box>
            </Paper>
        </Container>
        <AppFooter />
        </>
    );
};

export default WebPrivacyPolicyScreen;
